import { trackAppEvent } from './appEvents';
import type { AppEventName } from '@etap/shared';

declare global {
  interface Window {
    ym?: YMFn;
    __YM_ID__?: string | number;
  }
}

type YMFn = ((...args: unknown[]) => void) & { a?: unknown[][]; l?: number };

const SNIPPET_YM_ID = typeof window !== 'undefined' ? window.__YM_ID__ : undefined;
const METRIKA_ID = (import.meta.env.VITE_YM_ID || SNIPPET_YM_ID) as string | number | undefined;

let initialized = false;

const isBrowser = typeof window !== 'undefined';

const ensureMetrika = () => {
  if (initialized) return true;
  if (!isBrowser || !METRIKA_ID) return false;

  const w = window as typeof window & { ym?: YMFn; [key: string]: unknown };
  const d = document;

  if (typeof w.ym === 'function' && w.ym.l) {
    initialized = true;
    return true;
  }

  const ym: YMFn =
    w.ym ||
    (function(...args: unknown[]) {
      ym.a = ym.a || [];
      ym.a.push(args);
    } as YMFn);

  ym.l = Date.now();
  w.ym = ym;

  const script = d.createElement('script');
  script.async = true;
  script.src = 'https://mc.yandex.ru/metrika/tag.js';

  const alreadyLoaded = Array.from(d.getElementsByTagName('script')).some((el) =>
    (el as HTMLScriptElement).src?.includes('mc.yandex.ru/metrika/tag.js')
  );

  if (!alreadyLoaded) {
    const firstScript = d.getElementsByTagName('script')[0];
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      d.head?.appendChild(script);
    }
  }

  window.ym?.(METRIKA_ID, 'init', {
    clickmap: true,
    trackLinks: true,
    accurateTrackBounce: true,
    webvisor: true,
    trackHash: true,
  });

  initialized = true;
  return true;
};

export const initMetrica = () => {
  ensureMetrika();
};

export const trackHit = (path: string) => {
  if (!ensureMetrika()) return;
  window.ym?.(METRIKA_ID, 'hit', path);
};

/**
 * Событие уходит в два места сразу.
 *
 * Метрика отвечает на вопросы про трафик и работает даже без авторизации,
 * но её события не связать с user_id. Свой поток (app_events) связывается —
 * и только по нему можно посмотреть, что делал конкретный человек до того,
 * как перестал заходить. Разбирать это на два вызова в каждом компоненте
 * бессмысленно, поэтому фан-аут спрятан здесь.
 */
export const trackEvent = (name: string, params?: Record<string, unknown>) => {
  trackAppEvent(name as AppEventName, toEventProps(params));

  if (!ensureMetrika()) return;
  window.ym?.(METRIKA_ID, 'reachGoal', name, params);
};

/**
 * В свой поток пускаем только скаляры и режем длинные строки: props нужны
 * для признаков («есть теги», «категория выбрана»), а не для содержимого,
 * которое человек ввёл руками.
 */
const toEventProps = (
  params?: Record<string, unknown>,
): Record<string, string | number | boolean | null> | undefined => {
  if (!params) return undefined;

  const result: Record<string, string | number | boolean | null> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    if (value === null || typeof value === 'boolean' || typeof value === 'number') {
      result[key.slice(0, 40)] = value as string | number | boolean | null;
    } else if (typeof value === 'string') {
      result[key.slice(0, 40)] = value.slice(0, 64);
    }
  }

  return Object.keys(result).length > 0 ? result : undefined;
};
